import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import AgentPanel from "../components/AgentPanel";
import AgentEditorModal from "../components/AgentEditorModal";
import { EMAIL_KEY, clearToken, fetchAgents, fetchLeads } from "../lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorState, setEditorState] = useState(null); // { mode, agent?, presetGroup?, presetParent? }
  const email = localStorage.getItem(EMAIL_KEY) || "";

  const reloadAgents = useCallback(async () => {
    const data = await fetchAgents();
    setAgents(data);
    return data;
  }, []);

  const reloadLeads = useCallback(
    async (id) => {
      const target = id || selectedId;
      if (!target) return;
      try {
        const data = await fetchLeads(target);
        setLeads(data);
      } catch {
        setLeads([]);
      }
    },
    [selectedId],
  );

  useEffect(() => {
    (async () => {
      try {
        const data = await reloadAgents();
        const firstSelectable = data.find((a) => !data.some((c) => c.parent_id === a.id)) || data[0];
        if (firstSelectable) {
          setSelectedId(firstSelectable.id);
          const l = await fetchLeads(firstSelectable.id);
          setLeads(l);
        }
      } catch (err) {
        toast.error("Failed to load agents");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      try {
        const l = await fetchLeads(selectedId);
        setLeads(l);
      } catch {
        setLeads([]);
      }
    })();
  }, [selectedId]);

  const onLogout = () => {
    clearToken();
    navigate("/login", { replace: true });
  };

  const selectedAgent = agents.find((a) => a.id === selectedId) || null;
  const selectedHasChildren = useMemo(
    () => (selectedId ? agents.some((a) => a.parent_id === selectedId) : false),
    [agents, selectedId],
  );

  const openNew = (group, parentId) => {
    setEditorState({
      mode: "create",
      agent: {
        group: group || "projects",
        parent_id: parentId || null,
        accent_color: "teal",
        language: "English",
      },
    });
  };

  const openEdit = () => {
    if (!selectedAgent) return;
    setEditorState({ mode: "edit", agent: selectedAgent });
  };

  const onSaved = async (agent, action) => {
    setEditorState(null);
    const fresh = await reloadAgents();
    if (action === "delete") {
      const next = fresh.find((a) => !fresh.some((c) => c.parent_id === a.id)) || fresh[0];
      setSelectedId(next ? next.id : null);
      if (next) {
        const l = await fetchLeads(next.id);
        setLeads(l);
      } else {
        setLeads([]);
      }
    } else {
      setSelectedId(agent.id);
      if (action === "create") setLeads([]);
    }
  };

  return (
    <div className="min-h-screen flex bg-aot-bg text-aot-text">
      <Sidebar
        agents={agents}
        selectedId={selectedId}
        onSelect={setSelectedId}
        email={email}
        onLogout={onLogout}
        onNew={(group) => openNew(group)}
      />
      <div className="flex-1 min-w-0 flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-aot-muted text-sm font-mono">
            Loading console…
          </div>
        ) : selectedAgent ? (
          <AgentPanel
            key={selectedAgent.id}
            agent={selectedAgent}
            leads={leads}
            hasChildren={selectedHasChildren}
            onEdit={openEdit}
            onNewSubAgent={
              !selectedAgent.parent_id
                ? () => openNew(selectedAgent.group, selectedAgent.id)
                : undefined
            }
            reloadLeads={() => reloadLeads(selectedAgent.id)}
            reloadAgents={reloadAgents}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 p-8">
            <p className="font-display text-xl">No agents yet</p>
            <p className="text-sm text-aot-muted max-w-sm">
              Create your first agent to start having Claude search the web for leads.
            </p>
            <button
              onClick={() => openNew("projects")}
              data-testid="empty-create-agent-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-aot-teal text-aot-bg text-xs font-mono uppercase tracking-wider hover:opacity-90"
            >
              <Plus className="w-3.5 h-3.5" />
              New agent
            </button>
          </div>
        )}
      </div>

      {editorState && (
        <AgentEditorModal
          mode={editorState.mode}
          agent={editorState.agent}
          agents={agents}
          onClose={() => setEditorState(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
